import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, Row, Col, Badge, Button, Alert, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, 
  faDownload, 
  faFileContract,
  faCalendarAlt,
  faMoneyBillWave,
  faUser,
  faHome,
  faSignOutAlt,
  faKey
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import { contractService } from '../../services/contractService';
import { pdfService } from '../../services/pdfService';
import { userService } from '../../services/userService';
import { companyService } from '../../services/companyService';

const TenantContractDetails = () => {
  const { contractId } = useParams();
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  
  const [contract, setContract] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    fetchContractData();
  }, [contractId]);

  const fetchContractData = async () => {
    try {
      setLoading(true);
      
      // Get user profile
      const profile = await userService.getUserProfile();
      setUserProfile(profile);
      
      // Get contract details
      const contractData = await contractService.getContractById(contractId);
      setContract(contractData);
      
      // Fetch company information and brokerage settings
      if (contractData.companyId) {
        try {
          const [companyData, settingsData] = await Promise.all([
            companyService.getCompanyById(contractData.companyId),
            contractService.getBrokerageSettings(contractData.companyId)
          ]);
          setCompanyInfo(companyData);
          // Set logo: custom logo if available, otherwise use default URProperty logo
          setCompanyLogo((settingsData.logoEnabled && settingsData.logoUrl) ? settingsData.logoUrl : '/urproperty-logo.png');
        } catch (error) {
          console.error('Error fetching company data:', error);
          // Don't fail the entire request if company fetch fails
        }
      }
    } catch (error) {
      console.error('Error fetching contract data:', error);
      setError('Failed to load contract data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      setDownloading(true);
      setError('');
      
      // Generate PDF with proper parameters
      const pdf = await pdfService.generateContractPDF(contract, userProfile, null, companyLogo, companyInfo);
      
      // Download the PDF
      const filename = `contract_${contract?.contractId || contract?.id}_${new Date().toISOString().split('T')[0]}.pdf`;
      pdfService.downloadPDF(pdf, filename);
      
      setDownloading(false);
    } catch (error) {
      console.error('Error generating PDF:', error);
      setError('Failed to generate PDF: ' + (error.message || error));
      setDownloading(false);
    }
  };

  const formatCurrency = (amount, currency = 'USD') => {
    const symbols = {
      USD: '$',
      EUR: '€',
      GBP: '£',
      AED: 'د.إ',
      SAR: 'ر.س',
      PKR: '₨',
      INR: '₹'
    };
    return `${symbols[currency] || '$'}${parseFloat(amount || 0).toLocaleString()}`;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { variant: 'success', text: 'Active' },
      expired: { variant: 'danger', text: 'Expired' },
      terminated: { variant: 'secondary', text: 'Terminated' },
      pending: { variant: 'warning', text: 'Pending' }
    };
    
    const config = statusConfig[status] || { variant: 'secondary', text: status };
    return <Badge bg={config.variant}>{config.text}</Badge>;
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!contract) {
    return (
      <div className="container-fluid">
        <Alert variant="danger">
          Contract not found or you don't have permission to view it.
        </Alert>
        <Button as={Link} to="/tenant/dashboard" variant="primary">
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          Back to Dashboard
        </Button>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center">
          <Button 
            as={Link} 
            to="/tenant/dashboard" 
            variant="outline-secondary" 
            className="me-3"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </Button>
          <div>
            <h2 className="mb-1">Contract Details</h2>
            <p className="text-muted mb-0">Contract ID: {contract.contractId || contract.id}</p>
          </div>
        </div>
        <div className="d-flex gap-2">
          <Button 
            variant="outline-primary" 
            onClick={handleDownloadPDF}
            disabled={downloading}
          >
            <FontAwesomeIcon icon={faDownload} className="me-2" />
            {downloading ? 'Generating...' : 'Download PDF'}
          </Button>
          <Button variant="outline-danger" onClick={handleLogout}>
            <FontAwesomeIcon icon={faSignOutAlt} className="me-2" />
            Logout
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Row>
        {/* Contract Information */}
        <Col lg={8}>
          <Card className="border-0 shadow-sm mb-4">
            <Card.Header className="bg-transparent border-0">
              <h5 className="mb-0">
                <FontAwesomeIcon icon={faFileContract} className="text-primary me-2" />
                Contract Information
              </h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <label className="form-label text-muted">Contract ID</label>
                    <p className="h6">{contract.contractId || contract.id}</p>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <label className="form-label text-muted">Status</label>
                    <div>{getStatusBadge(contract.status)}</div>
                  </div>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <label className="form-label text-muted">
                      <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                      Start Date
                    </label>
                    <p className="h6">
                      {contract.contractStartDate ? new Date(contract.contractStartDate).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <label className="form-label text-muted">
                      <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                      End Date
                    </label>
                    <p className="h6">
                      {contract.contractEndDate ? new Date(contract.contractEndDate).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <label className="form-label text-muted">
                      <FontAwesomeIcon icon={faMoneyBillWave} className="me-1" />
                      Monthly Rent
                    </label>
                    <p className="h6 text-success">
                      {formatCurrency(
                        contract.monthlyRent || (contract.totalRentalAmount && contract.contractDuration ? contract.totalRentalAmount / contract.contractDuration : contract.totalRentalAmount / 12), 
                        contract.currency || 'USD'
                      )}
                    </p>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <label className="form-label text-muted">
                      <FontAwesomeIcon icon={faMoneyBillWave} className="me-1" />
                      Total Rental Amount
                    </label>
                    <p className="h6">
                      {formatCurrency(contract.totalRentalAmount || 0, contract.currency || 'USD')}
                    </p>
                  </div>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <label className="form-label text-muted">Number of Cheques</label>
                    <p className="h6">{contract.numberOfCheques || 'N/A'}</p>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <label className="form-label text-muted">Installment Amount</label>
                    <p className="h6 text-info">
                      {formatCurrency(contract.chequeAmount || 0, contract.currency || 'USD')}
                    </p>
                  </div>
                </Col>
              </Row>
              
              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <label className="form-label text-muted">Currency</label>
                    <p className="h6">{contract.currency || 'USD'}</p>
                  </div>
                </Col>
              </Row>

              {contract.brokerageFee && (
                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <label className="form-label text-muted">Admin Fees</label>
                      <p className="h6 text-warning">
                        {formatCurrency(contract.brokerageFee, contract.currency || 'USD')}
                      </p>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <label className="form-label text-muted">Total Amount</label>
                      <p className="h6 text-primary">
                        {formatCurrency(contract.totalAmount || 0, contract.currency || 'USD')}
                      </p>
                    </div>
                  </Col>
                </Row>
              )}
            </Card.Body>
          </Card>

          {/* Property Information */}
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-transparent border-0">
              <h5 className="mb-0">
                <FontAwesomeIcon icon={faHome} className="text-success me-2" />
                Property Information
              </h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <label className="form-label text-muted">Property Unit</label>
                    <p className="h6">{contract.propertyUnitId || 'N/A'}</p>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <label className="form-label text-muted">Property Group</label>
                    <p className="h6">{contract.propertyGroupId || 'N/A'}</p>
                  </div>
                </Col>
              </Row>

              {contract.notes && (
                <div className="mb-3">
                  <label className="form-label text-muted">Notes</label>
                  <p className="h6">{contract.notes}</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Sidebar */}
        <Col lg={4}>
          {/* Quick Actions */}
          <Card className="border-0 shadow-sm mb-4">
            <Card.Header className="bg-transparent border-0">
              <h6 className="mb-0">Quick Actions</h6>
            </Card.Header>
            <Card.Body>
              <div className="d-grid gap-2">
                <Button 
                  variant="primary" 
                  onClick={handleDownloadPDF}
                  disabled={downloading}
                >
                  <FontAwesomeIcon icon={faDownload} className="me-2" />
                  {downloading ? 'Generating PDF...' : 'Download Contract'}
                </Button>
                <Button as={Link} to="/tenant/dashboard" variant="outline-secondary">
                  <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                  Back to Dashboard
                </Button>
              </div>
            </Card.Body>
          </Card>

          {/* Contract Summary */}
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-transparent border-0">
              <h6 className="mb-0">Contract Summary</h6>
            </Card.Header>
            <Card.Body>
              <div className="d-flex justify-content-between mb-2">
                <span>Monthly Rent:</span>
                <strong>{formatCurrency(
                  contract.monthlyRent || (contract.totalRentalAmount && contract.contractDuration ? contract.totalRentalAmount / contract.contractDuration : contract.totalRentalAmount / 12), 
                  contract.currency || 'USD'
                )}</strong>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span>Duration:</span>
                <strong>{contract.contractDuration || contract.numberOfCheques || 0} months</strong>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span>Total Amount:</span>
                <strong>{formatCurrency(contract.totalRentalAmount || 0, contract.currency || 'USD')}</strong>
              </div>
              <hr />
              <div className="d-flex justify-content-between">
                <span>Status:</span>
                {getStatusBadge(contract.status)}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default TenantContractDetails; 